import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api.js';

/**
 * Confirming an address from an emailed link.
 *
 * The token in the query string is the whole of it, so this needs no
 * session and no typing: it is spent on arrival and the page says what
 * happened. It does not sign anyone in -- the password does that, and the
 * link never carries one.
 */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [username, setUsername] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // React runs effects twice in development, and the second run would
  // spend a token the first already spent. The server answers a spent
  // link kindly, so this is tidiness rather than correctness.
  const asked = useRef(false);

  useEffect(() => {
    if (!token || asked.current) return;
    asked.current = true;
    api.verifyEmail(token)
      .then((answer) => {
        setUsername(answer.username);
        setState('done');
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('failed');
      });
  }, [token]);

  return (
    <div className="signin">
      <div className="plate">
        <div className="hazard hazard-rule" role="presentation" />
        <div className="stack" style={{ padding: '1.5rem' }}>
          <h2>Confirm your account</h2>

          {!token ? (
            <p className="notice">
              That link is missing its token. Sign in to be sent another.
            </p>
          ) : state === 'working' ? (
            <p className="eyebrow">Checking that link</p>
          ) : state === 'done' ? (
            <>
              <p className="notice ok">
                {username ? `${username} is confirmed.` : 'Confirmed.'} You can
                sign in now.
              </p>
              <button onClick={() => navigate('/sign-in')}>Sign in</button>
            </>
          ) : (
            <>
              <p className="notice">{error}</p>
              <button className="secondary" onClick={() => navigate('/sign-in')}>
                Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
